import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import Sidebar, { clearSession } from "../components/Sidebar";
import { applyBillingEnumText, splitWords } from "../utils/enums";
import { isPhaseAdmin } from "../utils/role";
import {
  Dashboard6Styles, ModalShell, InlineModal, Field, PagePreloader, Avatar, StatusChip, CardHead, PageHeader, KpiStrip, Pagination, ColumnPicker, SegTabs,
} from "../components/ModalUI";

const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

const HOUSE_TYPE_IMAGES = {
  "1 Bedroom Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "1 Bedrooms Bungalow": "/assets/media/zangon/1bedroombungalo.jpg",
  "2 Bedroom Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "2 Bedrooms Bungalow": "/assets/media/zangon/2bedroom.jpg",
  "3 Bedrooms Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "3 Bedroom Bungalow": "/assets/media/zangon/3bedroomsbungalo.jpg",
  "4 Bedrooms Duplex": "/assets/media/zangon/4bd.jpg",
  "4 Bedroom Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "4 Bedrooms Bungalow": "/assets/media/zangon/4bedroombungalo.jpg",
  "5 Bedrooms Terrace": "/assets/media/zangon/5-light.jpg",
  "6 Bedroom Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedrooms Bungalow": "/assets/media/zangon/6bedroombungalow.jpg",
  "6 Bedroom Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
  "6 Bedrooms Terrace Duplex": "/assets/media/zangon/6bedroomterraceduple.jpg",
};

function getHouseImage(typeName) {
  if (!typeName) return "/assets/media/zangon/3bedroomsbungalo.jpg";
  const norm = typeName.toLowerCase().replace(/[^a-z0-9]/g, "").replace(/bedrooms?/g, "bedroom");
  if (norm.includes("1bedroom") || norm.includes("1bd")) {
    return "/assets/media/zangon/1bedroombungalo.jpg";
  }
  if (norm.includes("2bedroom") || norm.includes("2bd")) {
    return "/assets/media/zangon/2bedroom.jpg";
  }
  if (norm.includes("3bedroombungalow") || norm.includes("3bedroombungalo") || norm.includes("3bd")) {
    return "/assets/media/zangon/3bedroomsbungalo.jpg";
  }
  if (norm.includes("4bedroombungalow") || norm.includes("4bedroombungalo")) {
    return "/assets/media/zangon/4bedroombungalo.jpg";
  }
  if (norm.includes("4bedroomduplex") || norm.includes("4bd")) {
    return "/assets/media/zangon/4bd.jpg";
  }
  if (norm.includes("5bedroom")) {
    const isDark = document.documentElement.getAttribute("data-bs-theme") === "dark" || 
                   localStorage.getItem("kt_theme_mode") === "dark";
    return isDark ? "/assets/media/zangon/5-dark.jpg" : "/assets/media/zangon/5-light.jpg";
  }
  if (norm.includes("6bedroombungalow")) {
    return "/assets/media/zangon/6bedroombungalow.jpg";
  }
  if (norm.includes("6bedroomterraceduplex") || norm.includes("6bedroomterraceduple") || (norm.includes("6bedroom") && norm.includes("terrace"))) {
    return "/assets/media/zangon/6bedroomterraceduple.jpg";
  }
  return HOUSE_TYPE_IMAGES[typeName] || "/assets/media/zangon/3bedroomsbungalo.jpg";
}

function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const prev = getSession() || {};
  const next = { ...prev, ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtMoney(amount) {
  const n = Number(amount || 0);
  return `₦${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function getRentAmount(currentRent) {
  if (!currentRent) return 0;
  if (typeof currentRent === "number") return currentRent;
  if (typeof currentRent === "string") return Number(currentRent) || 0;
  if (typeof currentRent === "object") {
    return Number(currentRent.rentAmount || currentRent.amount || 0);
  }
  return 0;
}

function getDisplayComponents(components, rentAmount) {
  const list = [...(components || [])];
  const hasRent = list.some(c => Number(c.type) === 0 || (c.name && String(c.name).toLowerCase() === "rent"));
  const rentAmt = Number(rentAmount || 0);
  if (!hasRent && rentAmt > 0) {
    list.unshift({ type: 0, name: "Rent", amount: rentAmt });
  }
  return list;
}

function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

const BUILDING_PAGE_SIZE = 30;
const CARD_IMG_HEIGHT = 180;

function checkIsBuildingRentDue(building, upcomingRentTenants = []) {
  if (!building) return false;
  if (building.isRentDue || building.isDue || building.rentStatus === "Overdue" || building.rentStatus === "Due" || building.invoiceStatus === "Overdue" || building.invoiceStatus === "Pending") {
    return true;
  }
  if (upcomingRentTenants && upcomingRentTenants.length > 0) {
    const found = upcomingRentTenants.some((t) =>
      t.upcomingRentBuildings?.some(
        (b) => (b.buildingId && b.buildingId === building.id) || (b.buildingNumber && String(b.buildingNumber) === String(building.number))
      )
    );
    if (found) return true;
  }
  return false;
}

function checkIsBuildingAssigned(building, tenants = []) {
  if (!building) return false;
  if (
    building.isAssigned ||
    building.isOccupied ||
    building.status === "Occupied" ||
    building.status === "Assigned" ||
    building.currentTenantId ||
    building.currentTenantName ||
    building.tenantId ||
    building.tenantName ||
    building.occupantName
  ) {
    return true;
  }
  if (tenants && tenants.length > 0) {
    const bId = building.id || building.buildingId;
    const bNum = String(building.number || building.buildingNumber || "").trim();
    return tenants.some(t =>
      t.buildings?.some(tb => {
        const tbId = tb.id || tb.buildingId;
        const tbNum = String(tb.number || tb.buildingNumber || "").trim();
        return (tbId && bId && String(tbId) === String(bId)) || (tbNum && bNum && tbNum === bNum);
      })
    );
  }
  return false;
}

function sortBuildingsByPhase(buildingsList) {
  if (!buildingsList || !Array.isArray(buildingsList)) return [];
  return [...buildingsList].sort((a, b) => {
    const phaseA = String(a.phaseName || "");
    const phaseB = String(b.phaseName || "");
    const phaseComp = phaseA.localeCompare(phaseB, undefined, { numeric: true, sensitivity: "base" });
    if (phaseComp !== 0) return phaseComp;
    const numA = String(a.number || a.buildingNumber || "");
    const numB = String(b.number || b.buildingNumber || "");
    return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
  });
}

function sortBuildingsForAssignment(buildingsList, tenants = []) {
  if (!buildingsList || !Array.isArray(buildingsList)) return [];
  return [...buildingsList].sort((a, b) => {
    const isAssignedA = checkIsBuildingAssigned(a, tenants);
    const isAssignedB = checkIsBuildingAssigned(b, tenants);

    if (isAssignedA !== isAssignedB) {
      return isAssignedA ? 1 : -1;
    }

    const phaseA = String(a.phaseName || "");
    const phaseB = String(b.phaseName || "");
    const phaseComp = phaseA.localeCompare(phaseB, undefined, { numeric: true, sensitivity: "base" });
    if (phaseComp !== 0) return phaseComp;

    const numA = String(a.number || a.buildingNumber || "");
    const numB = String(b.number || b.buildingNumber || "");
    return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
  });
}

function DownloadRentLogModal({ modalRef, buildings = [], upcomingRentTenants = [], fmtMoney }) {
  const [options, setOptions] = useState({
    buildingNumber: true,
    phaseName: true,
    houseType: true,
    rentAmount: true,
    rentStatus: true,
    assignedTenant: true,
    tenantPhone: true,
    dueDate: true,
  });

  const handleDownload = () => {
    if (!buildings || buildings.length === 0) {
      toast.error("No rent data available to export");
      return;
    }

    const exportRows = buildings.map((b) => {
      const isDue = checkIsBuildingRentDue(b, upcomingRentTenants);
      const rentAmt = getRentAmount(b.currentRent);

      const tenantMatch = upcomingRentTenants.find((t) =>
        t.upcomingRentBuildings?.some(
          (ub) => (ub.buildingId && ub.buildingId === b.id) || (ub.buildingNumber && String(ub.buildingNumber) === String(b.number))
        )
      );

      const row = {};
      if (options.buildingNumber) row["Building Number"] = b.number || "—";
      if (options.phaseName) row["Phase"] = b.phaseName || "—";
      if (options.houseType) row["House Type"] = b.typeName || "—";
      if (options.rentAmount) row["Rent Amount"] = rentAmt ? fmtMoney(rentAmt) : "No Rent Set";
      if (options.rentStatus) row["Rent Status"] = isDue ? "Rent Due" : rentAmt ? "Paid / Active" : "No Rent Set";
      if (options.assignedTenant) {
        row["Assigned Tenant"] = tenantMatch ? `${tenantMatch.firstName || ""} ${tenantMatch.lastName || ""}`.trim() : b.currentTenantName || b.tenantName || "—";
      }
      if (options.tenantPhone) {
        row["Tenant Phone"] = tenantMatch?.phoneNumber || b.tenantPhone || "—";
      }
      if (options.dueDate) {
        const ubInfo = tenantMatch?.upcomingRentBuildings?.[0];
        row["Due Date"] = ubInfo?.dueDate ? new Date(ubInfo.dueDate).toLocaleDateString("en-GB") : "—";
      }
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rent Log");
    XLSX.writeFile(workbook, `Estate_Rent_Log_${new Date().toISOString().split("T")[0]}.xlsx`);

    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
    toast.success("Rent log exported successfully");
  };

  const closeModal = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-down"
      title="Download Rent Log"
      onClose={closeModal}
      width={640}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={closeModal}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleDownload}>
            <i className="ki-outline ki-file-down fs-5 me-2.5 text-white"></i> Download Export
          </button>
        </>
      }
    >
      <ColumnPicker options={options} setOptions={setOptions} />
    </ModalShell>
  );
}

function SetRentModal({ modalRef, estateId, buildings, buildingTypes, onSuccess, initialBuildingId }) {
  const { hide } = useBootstrapModal(modalRef);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    rentAmount: "",
    buildingId: "",
    applyToType: false,
    typeId: "",
  });
  const [extraCharges, setExtraCharges] = useState([]);

  useEffect(() => {
    if (initialBuildingId) {
      setForm(p => ({ ...p, buildingId: initialBuildingId, applyToType: false }));
    }
  }, [initialBuildingId]);

  useEffect(() => {
    if (form.applyToType) {
      if (form.typeId) {
        const stored = localStorage.getItem(`zangon_type_charges_${form.typeId}`);
        setExtraCharges(stored ? safeJsonParse(stored) || [] : []);

        const match = buildings.find(x => x.typeId === form.typeId);
        const rentAmt = match ? getRentAmount(match.currentRent) : 0;
        if (rentAmt > 0) {
          setForm(p => ({ ...p, rentAmount: rentAmt.toString() }));
        } else {
          setForm(p => ({ ...p, rentAmount: "" }));
        }
      } else {
        setExtraCharges([]);
      }
    } else {
      if (form.buildingId) {
        const stored = localStorage.getItem(`zangon_building_charges_${form.buildingId}`);
        setExtraCharges(stored ? safeJsonParse(stored) || [] : []);

        const b = buildings.find(x => x.id === form.buildingId);
        const rentAmt = b ? getRentAmount(b.currentRent) : 0;
        if (rentAmt > 0) {
          setForm(p => ({ ...p, rentAmount: rentAmt.toString() }));
        } else {
          setForm(p => ({ ...p, rentAmount: "" }));
        }
      } else {
        setExtraCharges([]);
      }
    }
  }, [form.buildingId, form.typeId, form.applyToType, buildings]);

  const resetAndClose = () => {
    hide();
    setForm({ rentAmount: "", buildingId: "", applyToType: false, typeId: "" });
    setExtraCharges([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rentAmount || Number(form.rentAmount) <= 0) return toast.error("Enter a valid rent amount.");
    if (!form.applyToType && !form.buildingId) return toast.error("Select a building.");
    if (form.applyToType && !form.typeId) return toast.error("Select a building type.");

    const formattedCharges = extraCharges.map(c => ({
      type: Number(c.type),
      name: c.name,
      amount: Number(c.amount)
    }));
    for (const c of formattedCharges) {
      if (!c.name.trim()) return toast.error("Charge name cannot be empty.");
      if (isNaN(c.amount) || c.amount <= 0) return toast.error("Charge amount must be greater than 0.");
    }

    setSaving(true);
    try {
      if (form.applyToType) {
        const matches = buildings.filter(b => b.typeId === form.typeId);
        if (matches.length === 0) { toast.error("No buildings found for the selected type."); setSaving(false); return; }
        const results = await Promise.allSettled(
          matches.map(b =>
            authFetch(`${API_BASE}/estates/${estateId}/buildings/${b.id}/update-rent`, {
              method: "PUT",
              body: JSON.stringify({
                estateId,
                buildingId: b.id,
                rentAmount: Number(form.rentAmount)
              }),
            })
          )
        );
        const failed = results.filter(r => r.status === "rejected").length;
        if (failed > 0) {
          toast.error(`${failed} building(s) failed to update.`);
        } else {
          toast.success(`Rent applied to ${matches.length} building(s) successfully.`);
          localStorage.setItem(`zangon_type_charges_${form.typeId}`, JSON.stringify(formattedCharges));
          matches.forEach(b => {
            localStorage.setItem(`zangon_building_charges_${b.id}`, JSON.stringify(formattedCharges));
          });
        }
      } else {
        const res = await authFetch(
          `${API_BASE}/estates/${estateId}/buildings/${form.buildingId}/update-rent`,
          {
            method: "PUT",
            body: JSON.stringify({
              estateId,
              buildingId: form.buildingId,
              rentAmount: Number(form.rentAmount)
            })
          }
        );
        const json = safeJsonParse(await res.text());
        if (!res.ok) throw new Error(json?.message || "Failed to update rent.");
        toast.success("Rent updated successfully.");
        localStorage.setItem(`zangon_building_charges_${form.buildingId}`, JSON.stringify(formattedCharges));
      }

      onSuccess?.();
      resetAndClose();
    } catch (err) {
      toast.error(err.message || "Failed to set rent.");
    } finally {
      setSaving(false);
    }
  };

  const selectedBuildingObj = !form.applyToType ? buildings.find(b => b.id === form.buildingId) : null;
  const selectedTypeObj = form.applyToType ? buildingTypes.find(t => t.id === form.typeId) : null;
  const previewImgUrl = selectedBuildingObj
    ? (selectedBuildingObj.imageUrl || getHouseImage(selectedBuildingObj.typeName))
    : selectedTypeObj
      ? (selectedTypeObj.imageUrl || getHouseImage(selectedTypeObj.name))
      : null;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-wallet"
      title="Set / Update Rent"
      onClose={resetAndClose}
      closeDisabled={saving}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={saving}>Cancel</button>
          <button type="submit" form="set-rent-form" className="btn btn-success d6-mbtn" disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : "Apply Rent"}
          </button>
        </>
      }
    >
      <form id="set-rent-form" onSubmit={handleSubmit}>
        {previewImgUrl && (
          <div className="rounded-3 overflow-hidden border border-gray-200 bg-light position-relative mb-5" style={{ height: 160 }}>
            <img src={previewImgUrl} alt="House preview" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <span className="position-absolute bottom-0 start-0 m-3 badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">
              {selectedBuildingObj ? `${selectedBuildingObj.number} — ${selectedBuildingObj.phaseName}` : selectedTypeObj?.name}
            </span>
          </div>
        )}

        <div className="d6m-seg mb-5">
          {[
            { label: "Single Building", val: false, icon: "ki-home-2" },
            { label: "All by Type", val: true, icon: "ki-abstract-26" },
          ].map(({ label, val, icon }) => (
            <button
              key={label}
              type="button"
              className={form.applyToType === val ? "active" : ""}
              onClick={() => setForm(p => ({ ...p, applyToType: val, buildingId: "", typeId: "" }))}
            >
              <i className={`ki-outline ${icon}`}></i>{label}
            </button>
          ))}
        </div>

        <div className="d-flex flex-column gap-4">
          {!form.applyToType ? (
            <Field label="Building" required>
              <select className="d6m-input" value={form.buildingId} onChange={e => setForm(p => ({ ...p, buildingId: e.target.value }))}>
                <option value="">Select a building...</option>
                {sortBuildingsByPhase(buildings).map(b => (
                  <option key={b.id} value={b.id}>{b.number} — {b.phaseName} ({b.typeName})</option>
                ))}
              </select>
            </Field>
          ) : (
            <Field
              label="Building type"
              required
              hint={form.typeId ? `${buildings.filter(b => b.typeId === form.typeId).length} buildings match this type.` : undefined}
            >
              <select className="d6m-input" value={form.typeId} onChange={e => setForm(p => ({ ...p, typeId: e.target.value }))}>
                <option value="">Select a type...</option>
                {buildingTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Rent amount (₦)" required hint="This creates a BuildingRentAssignment record. Any existing active record will be closed out automatically.">
            <input
              type="number" min="0" className="d6m-input" placeholder="e.g. 1500000"
              value={form.rentAmount} onChange={e => setForm(p => ({ ...p, rentAmount: e.target.value }))}
            />
          </Field>
        </div>

        <div className="d6m-section">
          <div className="d6m-section-head">
            <span>Default extra charges <span className="d6m-muted">(optional)</span></span>
            <button
              type="button"
              className="btn btn-light-success d6-mbtn d6-mbtn-sm"
              onClick={() => setExtraCharges(p => [...p, { type: 1, name: "Service Charge", amount: "" }])}
            >
              <i className="ki-outline ki-plus fs-6 me-2"></i>Add charge
            </button>
          </div>
          {extraCharges.length === 0 ? (
            <div className="d6m-empty">No default extra charges added. These charges will automatically populate when generating rent invoices.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {extraCharges.map((comp, index) => (
                <div key={index} className="row g-2 align-items-center">
                  <div className="col-12 col-sm-4">
                    <select
                      className="d6m-input"
                      value={comp.type}
                      onChange={e => {
                        const val = Number(e.target.value);
                        const defaultName = val === 1 ? "Service Charge" : "Other";
                        setExtraCharges(prev => prev.map((c, i) => i === index ? { ...c, type: val, name: c.name === "Service Charge" || c.name === "Other" || !c.name ? defaultName : c.name } : c));
                      }}
                    >
                      <option value={1}>Service Charge</option>
                      <option value={2}>Other</option>
                    </select>
                  </div>
                  <div className="col-6 col-sm-3">
                    <input
                      type="text"
                      className="d6m-input"
                      placeholder="Name (e.g. Waste)"
                      value={comp.name}
                      onChange={e => setExtraCharges(prev => prev.map((c, i) => i === index ? { ...c, name: e.target.value } : c))}
                    />
                  </div>
                  <div className="col-5 col-sm-3">
                    <input
                      type="number"
                      min="0"
                      className="d6m-input"
                      placeholder="Amount (₦)"
                      value={comp.amount}
                      onChange={e => setExtraCharges(prev => prev.map((c, i) => i === index ? { ...c, amount: e.target.value } : c))}
                    />
                  </div>
                  <div className="col-1 col-sm-2 text-end">
                    <button type="button" className="d6-icon-btn ms-auto" onClick={() => setExtraCharges(p => p.filter((_, i) => i !== index))} aria-label="Remove charge">
                      <i className="ki-outline ki-trash fs-5 text-danger"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="d6m-section">
          <div className="d6m-section-head"><span>Rent configuration preview</span></div>
          {form.rentAmount && Number(form.rentAmount) > 0 ? (
            <div className="d6m-invoice">
              <div className="d6m-amount">
                {fmtMoney(Number(form.rentAmount) + extraCharges.reduce((acc, c) => acc + Number(c.amount || 0), 0))}
              </div>
              <div className="d6m-muted mt-1">
                Total (est.) · {!form.applyToType
                  ? (buildings.find(b => b.id === form.buildingId)?.number || "No building selected")
                  : `${buildings.filter(b => b.typeId === form.typeId).length} building(s)`}
              </div>
              <div className="d6m-lines">
                <div className="d6m-line"><span>Rent amount</span><span>{fmtMoney(form.rentAmount)}</span></div>
                {extraCharges.map((c, idx) => (
                  <div key={idx} className="d6m-line">
                    <span>{c.name || (Number(c.type) === 1 ? "Service Charge" : "Other")} (default)</span>
                    <span>{c.amount ? fmtMoney(c.amount) : "₦0"}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="d6m-empty">Enter rent details to see a preview</div>
          )}
        </div>

        <div className="d6m-note">
          <i className="ki-outline ki-information-5"></i>
          <span>
            Setting a new rent closes the existing active assignment and creates a fresh record. Already-issued invoices are <strong>not affected</strong>.
          </span>
        </div>
      </form>
    </ModalShell>
  );
}

function getLocalDateStr(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function calculatePeriodEnd(startDateStr) {
  if (!startDateStr) return "";
  const parts = startDateStr.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return "";
  const [y, m, d] = parts;
  const date = new Date(Date.UTC(y + 1, m - 1, d));
  return date.toISOString().split("T")[0];
}

function calculateDueDate(startDateStr) {
  if (!startDateStr) return "";
  const parts = startDateStr.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return "";
  const [y, m, d] = parts;
  const date = new Date(Date.UTC(y, m - 1, d + 14));
  return date.toISOString().split("T")[0];
}

function GenerateInvoiceModal({ modalRef, estateId, tenants, buildings = [], onSuccess }) {
  const { hide } = useBootstrapModal(modalRef);
  const [saving, setSaving] = useState(false);
  const [tenantBuildings, setTenantBuildings] = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [components, setComponents] = useState([]);

  const today = getLocalDateStr();
  const defaultPeriodEnd = calculatePeriodEnd(today);
  const defaultDueDate = calculateDueDate(today);

  const [form, setForm] = useState({
    tenantId: "",
    buildingId: "",
    periodStart: today,
    periodEnd: defaultPeriodEnd,
    dueDate: defaultDueDate,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getTenantBuildings = useCallback((t) => {
    const list = [
      ...(t.buildings || []),
      ...(t.assignedBuildings || []),
      ...(t.currentBuildings || []),
      ...(t.assignedBuilding ? [t.assignedBuilding] : []),
    ];
    if (buildings && buildings.length > 0) {
      const fromBuildings = buildings.filter(
        b => (b.currentTenantId && String(b.currentTenantId) === String(t.id)) ||
             (b.tenantId && String(b.tenantId) === String(t.id)) ||
             (b.occupantId && String(b.occupantId) === String(t.id))
      );
      list.push(...fromBuildings);
    }
    const unique = [];
    const seen = new Set();
    for (const b of list) {
      if (!b) continue;
      const key = String(b.id || b.buildingId || b.number || b.buildingNumber || "");
      if (key && !seen.has(key)) {
        seen.add(key);
        unique.push(b);
      }
    }
    return unique;
  }, [buildings]);

  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return tenants;
    const rawQ = searchQuery.toLowerCase().trim();
    const cleanNum = rawQ.replace(/(house|building|apt|flat|unit|no\.?|#)\s*/gi, "").trim();

    return tenants.filter(t => {
      const name = String(t.name || `${t.firstName || ""} ${t.lastName || ""}`).toLowerCase();
      const email = String(t.email || "").toLowerCase();
      const phone = String(t.phone || t.phoneNumber || "").toLowerCase();

      if (name.includes(rawQ) || email.includes(rawQ) || phone.includes(rawQ)) {
        return true;
      }

      const tBuildings = getTenantBuildings(t);
      const houseMatch = tBuildings.some(b => {
        const num = String(b.number || b.buildingNumber || "").toLowerCase();
        const phase = String(b.phaseName || "").toLowerCase();
        const type = String(b.typeName || "").toLowerCase();
        const fullBuildingStr = `${phase} ${num} ${type} house ${num}`.toLowerCase();

        return (
          num.includes(rawQ) ||
          phase.includes(rawQ) ||
          type.includes(rawQ) ||
          fullBuildingStr.includes(rawQ) ||
          (cleanNum && num.includes(cleanNum)) ||
          (cleanNum && `${phase} ${num}`.toLowerCase().includes(cleanNum))
        );
      });

      if (houseMatch) return true;
      if (t.buildingNumber && String(t.buildingNumber).toLowerCase().includes(rawQ)) return true;
      if (t.phaseName && String(t.phaseName).toLowerCase().includes(rawQ)) return true;

      return false;
    });
  }, [tenants, searchQuery, getTenantBuildings]);

  useEffect(() => {
    if (!form.tenantId || !estateId) { setTenantBuildings([]); setForm(p => ({ ...p, buildingId: "" })); return; }
    setLoadingBuildings(true);
    authFetch(`${API_BASE}/estates/${estateId}/tenants/${form.tenantId}/details`)
      .then(r => r.ok ? r.json() : null)
      .then(applyBillingEnumText)
      .then(json => {
        const data = json?.data || {};
        let bList = data.assignedBuildings || data.currentBuildings || (data.assignedBuilding ? [data.assignedBuilding] : []);
        if ((!bList || bList.length === 0) && buildings && buildings.length > 0) {
          bList = buildings.filter(
            b => (b.currentTenantId && String(b.currentTenantId) === String(form.tenantId)) ||
                 (b.tenantId && String(b.tenantId) === String(form.tenantId)) ||
                 (b.occupantId && String(b.occupantId) === String(form.tenantId))
          );
        }
        setTenantBuildings(bList || []);
        if (bList && bList.length === 1) {
          setForm(p => ({ ...p, buildingId: bList[0].id || bList[0].buildingId || "" }));
        }
      })
      .catch(() => {
        const bList = buildings.filter(
          b => (b.currentTenantId && String(b.currentTenantId) === String(form.tenantId)) ||
               (b.tenantId && String(b.tenantId) === String(form.tenantId)) ||
               (b.occupantId && String(b.occupantId) === String(form.tenantId))
        );
        setTenantBuildings(bList);
        if (bList.length === 1) {
          setForm(p => ({ ...p, buildingId: bList[0].id || bList[0].buildingId || "" }));
        }
      })
      .finally(() => setLoadingBuildings(false));
  }, [form.tenantId, estateId, buildings]);

  useEffect(() => {
    if (!form.buildingId) {
      setComponents([]);
      return;
    }
    const stored = localStorage.getItem(`zangon_building_charges_${form.buildingId}`);
    if (stored) {
      setComponents(safeJsonParse(stored) || []);
    } else {
      setComponents([]);
    }
  }, [form.buildingId]);

  const handlePeriodStartChange = (val) => {
    setForm(p => ({
      ...p,
      periodStart: val,
      ...(val ? {
        periodEnd: calculatePeriodEnd(val),
        dueDate: calculateDueDate(val),
      } : {}),
    }));
  };

  const resetAndClose = () => {
    hide();
    const initToday = getLocalDateStr();
    setForm({
      tenantId: "",
      buildingId: "",
      periodStart: initToday,
      periodEnd: calculatePeriodEnd(initToday),
      dueDate: calculateDueDate(initToday),
    });
    setTenantBuildings([]);
    setSearchQuery("");
    setShowDropdown(false);
    setComponents([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tenantId) return toast.error("Select a tenant.");
    if (!form.buildingId) return toast.error("Select a building.");
    if (!form.periodStart) return toast.error("Enter a Period Start date.");
    if (!form.periodEnd) return toast.error("Enter a Period End date.");
    if (!form.dueDate) return toast.error("Enter a Due Date.");
    if (new Date(form.periodEnd) <= new Date(form.periodStart)) return toast.error("Period End must be after Period Start.");

    const formattedComponents = components.map(c => ({
      type: Number(c.type),
      name: c.name,
      amount: Number(c.amount)
    }));
    for (const c of formattedComponents) {
      if (!c.name.trim()) return toast.error("Component name cannot be empty.");
      if (isNaN(c.amount) || c.amount <= 0) return toast.error("Component amount must be greater than 0.");
    }

    setSaving(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/buildings/${form.buildingId}/tenants/${form.tenantId}/rent-invoices`,
        {
          method: "POST",
          body: JSON.stringify({
            estateId: estateId,
            buildingId: form.buildingId,
            tenantId: form.tenantId,
            periodStart: new Date(form.periodStart).toISOString(),
            periodEnd: new Date(form.periodEnd).toISOString(),
            dueDate: new Date(form.dueDate).toISOString(),
            components: formattedComponents,
          }),
        }
      );
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to generate invoice.");
      toast.success("Rent invoice generated successfully.");
      onSuccess?.();
      resetAndClose();
    } catch (err) {
      toast.error(err.message || "Failed to generate invoice.");
    } finally {
      setSaving(false);
    }
  };

  const selectedTenant = tenants.find(t => t.id === form.tenantId);
  const selectedBuilding = tenantBuildings.find(b => (b.id || b.buildingId) === form.buildingId);

  const componentsTotal = components.reduce((acc, c) => acc + Number(c.amount || 0), 0);
  const buildingRent = selectedBuilding ? getRentAmount(selectedBuilding.currentRent) : 0;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-document"
      title="Generate Rent Invoice"
      onClose={resetAndClose}
      closeDisabled={saving}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={saving}>Cancel</button>
          <button type="submit" form="generate-invoice-form" className="btn btn-success d6-mbtn" disabled={saving}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Generating…</> : "Generate Invoice"}
          </button>
        </>
      }
    >
      <form id="generate-invoice-form" onSubmit={handleSubmit}>
        <div ref={dropdownRef}>
          <Field label="Select tenant or house" required>
            <div className="position-relative">
              <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400 pe-none"></i>
              <input
                type="text"
                className="d6m-input ps-10"
                placeholder="Search by tenant name, house number, phase, phone…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(true);
                  if (form.tenantId) setForm(p => ({ ...p, tenantId: "", buildingId: "" }));
                }}
                onFocus={() => setShowDropdown(true)}
              />
              {form.tenantId && (
                <i className="ki-outline ki-check-circle fs-4 text-success position-absolute top-50 end-0 translate-middle-y me-3"></i>
              )}
            </div>
            {showDropdown && (
              <div className="d6m-list mt-2" style={{ maxHeight: 240, overflowY: "auto" }}>
                {filteredTenants.length > 0 ? filteredTenants.map(t => {
                  const tBuildings = getTenantBuildings(t);
                  const houseText = tBuildings.length > 0
                    ? tBuildings.map(b => `${b.phaseName ? b.phaseName + " — " : ""}${b.number || b.buildingNumber || ""}`).join(", ")
                    : t.buildingNumber ? `House: ${t.buildingNumber}` : null;

                  return (
                    <button
                      type="button"
                      key={t.id}
                      className="d6m-list-row"
                      onClick={() => {
                        const singleBId = tBuildings.length === 1 ? (tBuildings[0].id || tBuildings[0].buildingId || "") : "";
                        setForm(prev => ({ ...prev, tenantId: t.id, buildingId: singleBId }));
                        setSearchQuery(t.name + (houseText ? ` (${houseText})` : ""));
                        setShowDropdown(false);
                      }}
                    >
                      <Avatar src={t.avatar || t.imageUrl} name={t.name} size={36} />
                      <span className="min-w-0 flex-grow-1 text-start">
                        <span className="d6m-v d-block text-truncate">{t.name}</span>
                        <span className="d6m-muted d-block text-truncate">{t.email || t.phone || "No contact info"}</span>
                      </span>
                      {houseText && <span className="d6m-count text-nowrap" style={{ maxWidth: 180 }}>{houseText}</span>}
                    </button>
                  );
                }) : (
                  <div className="d6m-empty border-0">No tenants or houses found</div>
                )}
              </div>
            )}
          </Field>
        </div>

        <div className="d-flex flex-column gap-4 mt-4">
          <Field label="Select building" required>
            <select
              className="d6m-input"
              value={form.buildingId}
              onChange={e => setForm(p => ({ ...p, buildingId: e.target.value }))}
              disabled={!form.tenantId || loadingBuildings}
            >
              <option value="">
                {loadingBuildings
                  ? "Loading assigned buildings..."
                  : !form.tenantId
                    ? "Select a tenant first..."
                    : tenantBuildings.length === 0
                      ? "No buildings assigned to this tenant"
                      : "Choose a building..."}
              </option>
              {sortBuildingsByPhase(tenantBuildings).map(b => (
                <option key={b.id || b.buildingId} value={b.id || b.buildingId}>
                  {b.phaseName ? `${b.phaseName} — ` : ""}{b.number}
                </option>
              ))}
            </select>
          </Field>

          <div className="row g-4">
            <Field className="col-6" label="Period start" required>
              <input type="date" className="d6m-input" value={form.periodStart} onChange={e => handlePeriodStartChange(e.target.value)} />
            </Field>
            <Field className="col-6" label="Period end" required>
              <input type="date" className="d6m-input" value={form.periodEnd} min={form.periodStart || undefined} onChange={e => setForm(p => ({ ...p, periodEnd: e.target.value }))} />
            </Field>
          </div>

          <Field label="Due date" required>
            <input type="date" className="d6m-input" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} />
          </Field>
        </div>

        <div className="d6m-section">
          <div className="d6m-section-head">
            <span>Extra components <span className="d6m-muted">(optional)</span></span>
            <button
              type="button"
              className="btn btn-light-success d6-mbtn d6-mbtn-sm"
              onClick={() => setComponents(p => [...p, { type: 1, name: "Service Charge", amount: "" }])}
            >
              <i className="ki-outline ki-plus fs-6 me-2"></i>Add component
            </button>
          </div>
          {components.length === 0 ? (
            <div className="d6m-empty">No extra components added. Rent is added automatically by the backend.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {components.map((comp, index) => (
                <div key={index} className="row g-2 align-items-center">
                  <div className="col-12 col-sm-4">
                    <select
                      className="d6m-input"
                      value={comp.type}
                      onChange={e => {
                        const val = Number(e.target.value);
                        const defaultName = val === 1 ? "Service Charge" : "Other";
                        setComponents(prev => prev.map((c, i) => i === index ? { ...c, type: val, name: c.name === "Service Charge" || c.name === "Other" || !c.name ? defaultName : c.name } : c));
                      }}
                    >
                      <option value={1}>Service Charge</option>
                      <option value={2}>Other</option>
                    </select>
                  </div>
                  <div className="col-6 col-sm-3">
                    <input
                      type="text"
                      className="d6m-input"
                      placeholder="Name (e.g. Waste)"
                      value={comp.name}
                      onChange={e => setComponents(prev => prev.map((c, i) => i === index ? { ...c, name: e.target.value } : c))}
                    />
                  </div>
                  <div className="col-5 col-sm-3">
                    <input
                      type="number"
                      min="0"
                      className="d6m-input"
                      placeholder="Amount (₦)"
                      value={comp.amount}
                      onChange={e => setComponents(prev => prev.map((c, i) => i === index ? { ...c, amount: e.target.value } : c))}
                    />
                  </div>
                  <div className="col-1 col-sm-2 text-end">
                    <button type="button" className="d6-icon-btn ms-auto" onClick={() => setComponents(p => p.filter((_, i) => i !== index))} aria-label="Remove component">
                      <i className="ki-outline ki-trash fs-5 text-danger"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="d6m-section">
          <div className="d6m-section-head"><span>Invoice summary</span></div>
          {form.tenantId && form.buildingId ? (
            <div className="d6m-invoice">
              <div className="d-flex align-items-start justify-content-between gap-3">
                <div className="min-w-0">
                  <div className="d6m-v text-truncate">{selectedTenant?.name || "—"}</div>
                  <div className="d6m-muted">
                    {selectedBuilding ? `${selectedBuilding.phaseName ? selectedBuilding.phaseName + " — " : ""}${selectedBuilding.number}` : "—"}
                  </div>
                </div>
                <div className="text-end flex-shrink-0">
                  <div className="d6m-v">{form.dueDate ? fmtDate(form.dueDate) : "—"}</div>
                  <div className="d6m-muted">Due date</div>
                </div>
              </div>
              <div className="d6m-muted mt-2">
                Period: {form.periodStart ? fmtDate(form.periodStart) : "—"} → {form.periodEnd ? fmtDate(form.periodEnd) : "—"}
              </div>
              <div className="d6m-lines">
                <div className="d6m-line">
                  <span>Rent (automatic)</span>
                  <span>{buildingRent > 0 ? fmtMoney(buildingRent) : "Building active rent"}</span>
                </div>
                {components.map((c, i) => (
                  <div key={i} className="d6m-line">
                    <span>{c.name || (Number(c.type) === 1 ? "Service Charge" : "Other")}</span>
                    <span>{c.amount ? fmtMoney(c.amount) : "₦0"}</span>
                  </div>
                ))}
                <div className="d6m-line d6m-line-total">
                  <span>Total (est.)</span>
                  <span>
                    {buildingRent > 0
                      ? fmtMoney(buildingRent + componentsTotal)
                      : components.length > 0
                        ? `Rent + ${fmtMoney(componentsTotal)}`
                        : "Building active rent"}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="d6m-empty">Select a tenant and building to preview the invoice</div>
          )}
        </div>

        <div className="d6m-note">
          <i className="ki-outline ki-information-5"></i>
          <span>The system validates the tenant's active assignment, then snapshots the current rent onto the invoice.</span>
        </div>
      </form>
    </ModalShell>
  );
}

function AssignBuildingModal({ modalRef, estateId, tenants, primaryOccupants = [], buildings, onSuccess }) {
  const { hide } = useBootstrapModal(modalRef);
  const [saving, setSaving] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [selectedBuildingIds, setSelectedBuildingIds] = useState([]);
  const [rentPaymentSchedule] = useState(4); // 4 = Yearly
  const [buildingSearch, setBuildingSearch] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const occupantsList = useMemo(() => {
    return [
      ...tenants.map(t => ({ ...t, _type: "tenant" })),
      ...primaryOccupants.map(po => ({ ...po, _type: "primary-occupant" }))
    ];
  }, [tenants, primaryOccupants]);

  const filteredOccupants = useMemo(() => {
    if (!searchQuery) return occupantsList;
    const q = searchQuery.toLowerCase();
    return occupantsList.filter(o => {
      const nameMatch = (o.name && o.name.toLowerCase().includes(q)) ||
        (o.firstName && `${o.firstName} ${o.lastName || ""}`.toLowerCase().includes(q));
      const emailMatch = o.email && o.email.toLowerCase().includes(q);
      const phoneMatch = o.phone && o.phone.toLowerCase().includes(q);
      const bList = o.assignedBuildings || (o.assignedBuilding ? [o.assignedBuilding] : []);
      const houseMatch = bList.some(b => String(b.number || b.buildingNumber || "").toLowerCase().includes(q)) ||
        (o.currentBuildingNumber && String(o.currentBuildingNumber).toLowerCase().includes(q));
      return nameMatch || emailMatch || phoneMatch || houseMatch;
    });
  }, [occupantsList, searchQuery]);

  const resetAndClose = () => {
    hide();
    setTenantId("");
    setSelectedBuildingIds([]);
    setBuildingSearch("");
    setSearchQuery("");
    setShowDropdown(false);
  };

  const toggleBuilding = (bId) => {
    setSelectedBuildingIds(prev =>
      prev.includes(bId) ? prev.filter(id => id !== bId) : [...prev, bId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tenantId) return toast.error("Select a tenant.");
    if (selectedBuildingIds.length === 0) return toast.error("Select at least one building.");

    setSaving(true);
    try {
      const res = await authFetch(
        `${API_BASE}/estates/${estateId}/tenants/${tenantId}/assign-buildings`,
        {
          method: "POST",
          body: JSON.stringify({
            estateId: estateId,
            tenantId: tenantId,
            buildingIds: selectedBuildingIds,
            rentSchedule: Number(rentPaymentSchedule),
          }),
        }
      );
      const json = safeJsonParse(await res.text());
      if (!res.ok) throw new Error(json?.message || "Failed to assign buildings.");
      toast.success("Buildings assigned successfully.");
      onSuccess?.();
      resetAndClose();
    } catch (err) {
      toast.error(err.message || "Failed to assign buildings.");
    } finally {
      setSaving(false);
    }
  };

  const filteredBuildings = useMemo(() => {
    const list = buildings.filter(b => {
      const q = buildingSearch.trim().toLowerCase();
      const bNum = String(b.number || b.buildingNumber || "").toLowerCase();
      const phase = String(b.phaseName || "").toLowerCase();
      const type = String(b.typeName || "").toLowerCase();
      return !q || bNum.includes(q) || phase.includes(q) || type.includes(q);
    });
    return sortBuildingsForAssignment(list, tenants);
  }, [buildings, buildingSearch, tenants]);

  const selectedList = selectedBuildingIds.map(id => buildings.find(x => x.id === id)).filter(Boolean);
  const firstBuilding = selectedList[0];
  const firstImgUrl = firstBuilding ? (firstBuilding.imageUrl || getHouseImage(firstBuilding.typeName)) : null;
  const selectedTenantName = tenants.find(t => t.id === tenantId)?.name;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-home-2"
      title="Assign Building to Tenant"
      onClose={resetAndClose}
      closeDisabled={saving}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={saving}>Cancel</button>
          <button type="submit" form="assign-building-form" className="btn btn-success d6-mbtn" disabled={saving || selectedBuildingIds.length === 0}>
            {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Assigning…</> : "Assign Building"}
          </button>
        </>
      }
    >
      <form id="assign-building-form" onSubmit={handleSubmit}>
        {firstImgUrl && (
          <div className="rounded-3 overflow-hidden border border-gray-200 bg-light position-relative mb-5" style={{ height: 160 }}>
            <img src={firstImgUrl} alt="Selected House" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            <span className="position-absolute bottom-0 start-0 m-3 badge bg-white text-gray-900 border border-gray-200 fs-8 fw-bold rounded-pill px-3 py-1">
              {selectedList.length === 1 ? `${firstBuilding.number} — ${firstBuilding.phaseName}` : `${firstBuilding.number} (+${selectedList.length - 1} more)`}
            </span>
          </div>
        )}

        <div ref={dropdownRef}>
          <Field label="Select tenant" required>
            <div className="position-relative">
              <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400 pe-none"></i>
              <input
                type="text"
                className="d6m-input ps-10"
                placeholder="Type name, email or phone…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(true);
                  if (tenantId) setTenantId("");
                }}
                onFocus={() => setShowDropdown(true)}
              />
              {tenantId && (
                <i className="ki-outline ki-check-circle fs-4 text-success position-absolute top-50 end-0 translate-middle-y me-3"></i>
              )}
            </div>
            {showDropdown && (
              <div className="d6m-list mt-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                {filteredOccupants.length > 0 ? filteredOccupants.map(o => (
                  <button
                    type="button"
                    key={`${o._type}-${o.id}`}
                    className="d6m-list-row"
                    onClick={() => {
                      setTenantId(o.id);
                      setSearchQuery(o.name);
                      setShowDropdown(false);
                    }}
                  >
                    <Avatar src={o.avatar || o.imageUrl} name={o.name} size={36} />
                    <span className="min-w-0 flex-grow-1 text-start">
                      <span className="d6m-v d-block text-truncate">{o.name}</span>
                      <span className="d6m-muted d-block text-truncate">{o.email || o.phone || "No contact info"}</span>
                    </span>
                    <StatusChip color={o._type === "primary-occupant" ? "#16A34A" : "#2563EB"}>
                      {o._type === "primary-occupant" ? "Primary Occupant" : "Tenant"}
                    </StatusChip>
                  </button>
                )) : (
                  <div className="d6m-empty border-0">No occupants found</div>
                )}
              </div>
            )}
          </Field>
        </div>

        <div className="d6m-section">
          <div className="d6m-section-head">
            <span>Select buildings <span className="text-danger">*</span></span>
            {selectedBuildingIds.length > 0 && <span className="d6m-count">{selectedBuildingIds.length}</span>}
          </div>
          <div className="position-relative mb-3">
            <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
            <input
              type="text"
              className="d6m-input ps-10"
              placeholder="Search buildings..."
              value={buildingSearch}
              onChange={e => setBuildingSearch(e.target.value)}
            />
          </div>
          {filteredBuildings.length === 0 ? (
            <div className="d6m-empty">No buildings match search</div>
          ) : (
            <div className="d6m-list" style={{ maxHeight: 260, overflowY: "auto" }}>
              {filteredBuildings.map(b => {
                const isChecked = selectedBuildingIds.includes(b.id);
                const isAssigned = checkIsBuildingAssigned(b, tenants);
                const assignedTenantName = b.currentTenantName || b.tenantName || b.occupantName;

                return (
                  <label
                    key={b.id}
                    className={`d6m-list-row d6m-check-row ${isChecked ? "active" : ""}`}
                    style={isAssigned ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isAssigned}
                      onChange={() => { if (!isAssigned) toggleBuilding(b.id); }}
                    />
                    <span className="d6m-check" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
                    </span>
                    <span className="min-w-0 flex-grow-1">
                      <span className="d6m-v d-block text-truncate">{b.number} — {b.phaseName} ({b.typeName})</span>
                    </span>
                    {isAssigned && (
                      <StatusChip color="#DC2626">{assignedTenantName ? `Assigned to ${assignedTenantName}` : "Assigned"}</StatusChip>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="d6m-section">
          <div className="d6m-section-head"><span>Assignment preview</span></div>
          {tenantId && selectedBuildingIds.length > 0 ? (
            <div className="d6m-invoice">
              <div className="d6m-v">{selectedTenantName}</div>
              <div className="d6m-muted">Payment schedule: Yearly</div>
              <div className="d-flex flex-wrap gap-2 mt-3">
                {selectedBuildingIds.map(bId => {
                  const b = buildings.find(x => x.id === bId);
                  return <StatusChip key={bId} color="#16A34A">{b ? b.number : "Unknown"}</StatusChip>;
                })}
              </div>
            </div>
          ) : (
            <div className="d6m-empty">Select tenant and building to preview assignment</div>
          )}
        </div>

        <div className="d6m-note">
          <i className="ki-outline ki-information-5"></i>
          <span>This assigns the selected building(s) to the tenant with a yearly payment schedule.</span>
        </div>
      </form>
    </ModalShell>
  );
}

function HouseRentDetailsModal({ modalRef, estateId, buildingId, buildings, tenants, buildingTypes, onEditRent, onViewInvoice, onViewPayment }) {
  const { hide } = useBootstrapModal(modalRef);
  const [activeTab, setActiveTab] = useState("invoices");
  const [loading, setLoading] = useState(false);
  const [activeRent, setActiveRent] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);

  const building = useMemo(() => buildings.find(b => b.id === buildingId) || null, [buildings, buildingId]);
  const tenant = useMemo(() => {
    if (!buildingId) return null;
    return tenants.find(t =>
      t.buildings?.some(tb =>
        (tb.id || tb.buildingId) === buildingId ||
        (building && tb.number === building.number && tb.phaseName === building.phaseName)
      )
    );
  }, [tenants, buildingId, building]);

  const storedComponents = useMemo(() => {
    if (!buildingId) return [];
    const stored = localStorage.getItem(`zangon_building_charges_${buildingId}`);
    return stored ? safeJsonParse(stored) || [] : [];
  }, [buildingId]);

  const buildingType = useMemo(() => {
    if (!building) return null;
    return buildingTypes.find(t => t.id === building.typeId) || null;
  }, [buildingTypes, building]);

  useEffect(() => {
    if (!buildingId || !estateId) return;

    let isSubscribed = true;
    setLoading(true);
    setActiveRent(null);
    setInvoices([]);
    setPayments([]);

    const loadData = async () => {
      try {
        const rentRes = await authFetch(`${API_BASE}/estates/${estateId}/buildings/${buildingId}/active-rent`).catch(() => null);
        let rentData = null;
        if (rentRes && rentRes.ok) {
          const rentJson = safeJsonParse(await rentRes.text());
          rentData = rentJson?.data || null;
        }

        let invList = [];
        let payList = [];
        if (tenant) {
          const [invRes, payRes] = await Promise.all([
            authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenant.id}/rent-invoices`),
            authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenant.id}/payments`).catch(() => null)
          ]);

          const invJson = invRes.ok ? safeJsonParse(await invRes.text()) : null;
          const payJson = payRes && payRes.ok ? safeJsonParse(await payRes.text()) : null;

          const rawInvoices = parseArrayData(invJson);
          const rawPayments = parseArrayData(payJson);

          invList = rawInvoices.filter(inv => (inv.buildingId === buildingId) || (building && inv.buildingNumber === building.number));
          payList = rawPayments.filter(pay => (pay.buildingId === buildingId) || (building && pay.buildingNumber === building.number));

          invList.sort((a, b) => new Date(b.issuedAt || b.createdDate || 0) - new Date(a.issuedAt || a.createdDate || 0));
          payList.sort((a, b) => new Date(b.paidAt || 0) - new Date(a.paidAt || 0));
        }

        if (!isSubscribed) return;

        setActiveRent(rentData);
        setInvoices(invList);
        setPayments(payList);
      } catch (err) {
        console.error("Failed to load house rent details", err);
      } finally {
        if (isSubscribed) setLoading(false);
      }
    };

    loadData();
    return () => { isSubscribed = false; };
  }, [estateId, buildingId, tenant, building]);

  const handleClose = () => {
    hide();
  };

  const imageUrl = buildingType?.imageUrl || (building ? getHouseImage(building.typeName) : "/assets/media/image.png");

  const invStatusColor = (st) => (st === "Paid" ? "#16A34A" : st === "Created" ? "#D97706" : "#DC2626");
  const payStatusColor = (st) => (["Successful", "Paid"].includes(st) ? "#16A34A" : ["New", "Pending"].includes(st) ? "#2563EB" : "#DC2626");

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-home-2"
      title="House Details & Rent History"
      onClose={handleClose}
      width={760}
      subheader={
        building && (
          <div className="d6m-tabs">
            <button type="button" className={activeTab === "invoices" ? "active" : ""} onClick={() => setActiveTab("invoices")}>
              Rent Invoices<span className="d6m-count ms-2">{invoices.length}</span>
            </button>
            <button type="button" className={activeTab === "payments" ? "active" : ""} onClick={() => setActiveTab("payments")}>
              Payment History<span className="d6m-count ms-2">{payments.length}</span>
            </button>
          </div>
        )
      }
      footer={
        building ? (
          <div className="d-flex align-items-center justify-content-between w-100 gap-2 flex-wrap">
            <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>Close</button>
            <button
              type="button"
              className="btn btn-success d6-mbtn"
              onClick={() => { handleClose(); onEditRent(building.id); }}
            >
              <i className="ki-outline ki-wallet fs-5 me-2.5 text-white"></i>Configure / Update Rent
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose}>Close</button>
        )
      }
    >
      {!building ? (
        <div className="d6m-empty"><span className="spinner-border spinner-border-sm text-success me-2"></span>Loading details...</div>
      ) : (
        <>
          <div className="d6m-property">
            <img src={imageUrl} alt={building.typeName} />
            <div className="min-w-0 flex-grow-1">
              <div className="d6m-v text-truncate">{building.typeName}</div>
              <div className="d6m-muted">House: <strong className="text-gray-900">{building.number}</strong></div>
            </div>
            <StatusChip color="#16A34A">{building.phaseName}</StatusChip>
          </div>

          <div className="d6m-section">
            <div className="d6m-section-head"><span>Active rent</span></div>
            {activeRent ? (
              <div className="d6m-invoice">
                <div className="d6m-amount">{fmtMoney(activeRent.rentAmount)}</div>
                <div className="d6m-muted mt-1">Assigned {fmtDate(activeRent.createdDate)}</div>
                {storedComponents && storedComponents.length > 0 && (
                  <div className="d6m-lines">
                    {storedComponents.map((c, i) => (
                      <div key={i} className="d6m-line"><span>{c.name}</span><span>{fmtMoney(c.amount)}</span></div>
                    ))}
                    <div className="d6m-line d6m-line-total">
                      <span>Total monthly (est.)</span>
                      <span>{fmtMoney(Number(activeRent.rentAmount) + storedComponents.reduce((sum, c) => sum + Number(c.amount || 0), 0))}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="d6m-empty">No active rent configured for this house</div>
            )}
          </div>

          <div className="d6m-section">
            <div className="d6m-section-head"><span>Occupant / tenant</span></div>
            {tenant ? (() => {
              const avatarUrl = tenant.avatar || tenant.imageUrl || null;
              const houseNum = tenant.buildings?.map(b => b.number).filter(Boolean).join(", ") || tenant.currentBuildings?.map(b => b.number).filter(Boolean).join(", ") || building?.number || "—";
              return (
                <Link
                  to={`/tenants/${tenant.id}`}
                  className="d6-card border-hover-success p-3 d-flex align-items-center gap-3 text-decoration-none cursor-pointer transition-all"
                  onClick={handleClose}
                >
                  <Avatar src={avatarUrl} name={tenant.name} size={44} />
                  <div className="flex-grow-1 min-w-0">
                    <div className="d6m-name fs-7 text-truncate">{tenant.name}</div>
                    <div className="d6m-muted text-truncate">{tenant.email}</div>
                    <div className="d6m-muted">House: {houseNum}</div>
                  </div>
                  <span className="d6-icon-btn"><i className="ki-outline ki-arrow-up-right fs-5"></i></span>
                </Link>
              );
            })() : (
              <div className="d6m-empty">No active tenant assigned to this house</div>
            )}
          </div>

          <div className="d6m-section">
            <div className="d6m-section-head">
              <span>{activeTab === "invoices" ? "Rent invoices" : "Payment history"}</span>
            </div>
            {loading ? (
              <div className="d6m-empty"><span className="spinner-border spinner-border-sm text-success me-2"></span>Loading transactions...</div>
            ) : activeTab === "invoices" ? (
              invoices.length === 0 ? (
                <div className="d6m-empty">No rent invoices generated for this house</div>
              ) : (
                <div className="d6m-list">
                  {invoices.map((inv, idx) => {
                    const isPaid = inv.status === "Paid";
                    const overdue = !isPaid && inv.dueDate && new Date(inv.dueDate) < new Date();
                    return (
                      <div key={inv.id || idx} className="d6m-list-row" style={{ cursor: "default" }}>
                        <span className="min-w-0 flex-grow-1 text-start">
                          <span className="d6m-v d-block text-truncate">
                            {inv.periodStart ? fmtDate(inv.periodStart) : "—"} → {inv.periodEnd ? fmtDate(inv.periodEnd) : "—"}
                          </span>
                          <span className={`d6m-muted d-block ${overdue ? "text-danger" : ""}`}>Due {fmtDate(inv.dueDate)}</span>
                        </span>
                        <span className="d6m-v flex-shrink-0">{fmtMoney(inv.invoiceTotalAmount || inv.totalAmount || inv.rentAmount)}</span>
                        <StatusChip color={invStatusColor(inv.status)}>{inv.status || "—"}</StatusChip>
                        <button type="button" className="d6-icon-btn" onClick={() => onViewInvoice(inv)} title="View details">
                          <i className="ki-outline ki-eye fs-5"></i>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )
            ) : payments.length === 0 ? (
              <div className="d6m-empty">No payment records found for this house</div>
            ) : (
              <div className="d6m-list">
                {payments.map((p, idx) => (
                  <div key={p.id || idx} className="d6m-list-row" style={{ cursor: "default" }}>
                    <span className="min-w-0 flex-grow-1 text-start">
                      <span className="d6m-v d-block text-truncate" title={p.gatewayReference || p.merchantReference}>
                        {p.gatewayReference || p.merchantReference || "—"}
                      </span>
                      <span className="d6m-muted d-block">{fmtDate(p.paidAt)}</span>
                    </span>
                    <span className="d6m-v text-success flex-shrink-0">{fmtMoney(p.paymentTotalAmount || p.amountPaid)}</span>
                    <StatusChip color={payStatusColor(p.status)}>{p.status || "—"}</StatusChip>
                    <button type="button" className="d6-icon-btn" onClick={() => onViewPayment(p)} title="View payment details">
                      <i className="ki-outline ki-eye fs-5"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </ModalShell>
  );
}

function InvoiceDetailModal({ invoice, onClose }) {
  if (!invoice) return null;

  const isPaid = ["Paid"].includes(invoice.status);
  const statusColor = isPaid ? "#16A34A"
    : invoice.status === "Created" ? "#D97706"
      : invoice.status === "Overdue" ? "#DC2626" : "#a1a1aa";

  const dispComponents = getDisplayComponents(invoice.components, invoice.rentAmount);
  const total = invoice.invoiceTotalAmount || invoice.totalAmount || invoice.rentAmount;
  const overdue = !isPaid && invoice.dueDate && new Date(invoice.dueDate) < new Date();

  return (
    <InlineModal
      open
      icon="ki-document"
      title="Invoice Details"
      onClose={onClose}
      width={640}
      footer={<button type="button" className="btn btn-light d6-mbtn" onClick={onClose}>Close</button>}
    >
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div className="d6m-amount">{fmtMoney(total)}</div>
          <div className="d6m-muted mt-1">{invoice.tenantName || "Invoice total"}</div>
        </div>
        <StatusChip color={statusColor}>{invoice.status || "—"}</StatusChip>
      </div>

      <div className="d6m-kv d6m-kv-4 mt-5">
        <div className="d6m-kv-cell">
          <span className="d6m-k">Building</span>
          <span className="d6m-v">{invoice.buildingNumber || "—"}</span>
        </div>
        <div className="d6m-kv-cell">
          <span className="d6m-k">Rent schedule</span>
          <span className="d6m-v">{splitWords(invoice.rentSchedule || invoice.rentPaymentSchedule) || "—"}</span>
        </div>
        <div className="d6m-kv-cell">
          <span className="d6m-k">Period</span>
          <span className="d6m-v">
            {invoice.periodStart ? fmtDate(invoice.periodStart) : "—"}
            {invoice.periodEnd ? ` → ${fmtDate(invoice.periodEnd)}` : ""}
          </span>
        </div>
        <div className="d6m-kv-cell">
          <span className="d6m-k">Due date</span>
          <span className={`d6m-v ${overdue ? "text-danger" : ""}`}>{fmtDate(invoice.dueDate)}</span>
        </div>
      </div>

      {invoice.paidAt && (
        <div className="d6m-muted mt-3">Paid on <strong className="text-gray-900">{fmtDate(invoice.paidAt)}</strong></div>
      )}

      <div className="d6m-section">
        <div className="d6m-section-head"><span>{dispComponents.length > 0 ? "Invoice components" : "Rent amount (snapshotted)"}</span></div>
        <div className="d6m-invoice">
          <div className="d6m-lines" style={{ marginTop: 0 }}>
            {dispComponents.length > 0 ? (
              dispComponents.map((comp, idx) => (
                <div key={idx} className="d6m-line"><span>{comp.name}</span><span>{fmtMoney(comp.amount)}</span></div>
              ))
            ) : (
              <div className="d6m-line"><span>Rent</span><span>{fmtMoney(invoice.rentAmount)}</span></div>
            )}
            <div className="d6m-line d6m-line-total"><span>Total invoice amount</span><span>{fmtMoney(total)}</span></div>
          </div>
        </div>
      </div>
    </InlineModal>
  );
}

function PaymentReceiptModal({ payment, onClose }) {
  if (!payment) return null;

  const isSuccess = ["Successful", "Paid"].includes(payment.status);
  const statusColor = isSuccess ? "#16A34A"
    : ["New", "Pending"].includes(payment.status) ? "#2563EB"
      : payment.status === "Reversed" ? "#D97706" : "#DC2626";

  const payComps = getDisplayComponents(payment.components, payment.rentAmount || payment.amount);

  return (
    <InlineModal
      open
      icon="ki-wallet"
      title="Payment Details"
      onClose={onClose}
      width={640}
      footer={<button type="button" className="btn btn-light d6-mbtn" onClick={onClose}>Close</button>}
    >
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div>
          <div className="d6m-amount">{fmtMoney(payment.paymentTotalAmount || payment.amountPaid)}</div>
          <div className="d6m-muted mt-1">{payment.tenantName || "Total paid"}</div>
        </div>
        <StatusChip color={statusColor}>{payment.status || "—"}</StatusChip>
      </div>

      <div className="d6m-section">
        <div className="d6m-section-head"><span>{payComps.length > 0 ? "Payment components" : "Amount"}</span></div>
        <div className="d6m-invoice">
          <div className="d6m-lines" style={{ marginTop: 0 }}>
            {payComps.length > 0 ? (
              <>
                {payComps.map((comp, idx) => (
                  <div key={idx} className="d6m-line"><span>{comp.name}</span><span>{fmtMoney(comp.amount)}</span></div>
                ))}
                <div className="d6m-line d6m-line-total">
                  <span>Invoice total</span>
                  <span>{fmtMoney(payment.invoiceTotalAmount || (payment.paymentTotalAmount - payment.transactionCharge) || payment.amountPaid)}</span>
                </div>
              </>
            ) : (
              <div className="d6m-line"><span>Amount paid</span><span>{fmtMoney(payment.amountPaid)}</span></div>
            )}
            {payment.transactionCharge > 0 && (
              <div className="d6m-line"><span>Transaction charge</span><span>{fmtMoney(payment.transactionCharge)}</span></div>
            )}
            <div className="d6m-line d6m-line-total"><span>Total paid amount</span><span>{fmtMoney(payment.paymentTotalAmount || payment.amountPaid)}</span></div>
          </div>
        </div>
      </div>

      <div className="d6m-kv d6m-kv-4 mt-5">
        <div className="d6m-kv-cell">
          <span className="d6m-k">Merchant reference</span>
          <span className="d6m-v text-break">{payment.merchantReference || "—"}</span>
        </div>
        <div className="d6m-kv-cell">
          <span className="d6m-k">Gateway reference</span>
          <span className="d6m-v text-break">{payment.gatewayReference || "—"}</span>
        </div>
        <div className="d6m-kv-cell">
          <span className="d6m-k">Payment date</span>
          <span className="d6m-v">{fmtDate(payment.paidAt)}</span>
        </div>
        <div className="d6m-kv-cell">
          <span className="d6m-k">Tenant</span>
          <span className="d6m-v">{payment.tenantName || "—"}</span>
        </div>
      </div>

      {payment.receiptUrl && (
        <a href={payment.receiptUrl} target="_blank" rel="noreferrer" className="btn btn-success d6-mbtn w-100 mt-5">
          <i className="ki-outline ki-exit-down fs-5 me-2.5 text-white"></i>Download Receipt
        </a>
      )}
    </InlineModal>
  );
}

export default function RentManagement() {
  const session = getSession();
  const estateId = session?.estateId || "";

  const [pageReady, setPageReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [tenants, setTenants] = useState([]);
  const [primaryOccupants, setPrimaryOccupants] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const [dashboardSummary, setDashboardSummary] = useState({
    totalRentCollected: 0,
    currentMonthRentCollected: 0,
    pendingRent: 0,
  });

  const [buildingSearch, setBuildingSearch] = useState("");
  const [buildingPhaseFilter, setBuildingPhaseFilter] = useState("all");
  const [buildingTypeFilter, setBuildingTypeFilter] = useState("all");
  const [buildingRentFilter, setBuildingRentFilter] = useState("all");
  const [buildingSortBy, setBuildingSortBy] = useState("houseNumber");
  const [buildingPage, setBuildingPage] = useState(1);

  const [upcomingRentTenants, setUpcomingRentTenants] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);

  const setRentModalRef = useRef(null);
  const generateInvoiceModalRef = useRef(null);
  const assignBuildingModalRef = useRef(null);
  const houseRentDetailsModalRef = useRef(null);
  const downloadModalRef = useRef(null);

  const setRentModal = useBootstrapModal(setRentModalRef);
  const generateInvoiceModal = useBootstrapModal(generateInvoiceModalRef);
  const assignBuildingModal = useBootstrapModal(assignBuildingModalRef);
  const houseRentDetailsModal = useBootstrapModal(houseRentDetailsModalRef);
  const downloadModal = useBootstrapModal(downloadModalRef);

  const [viewingHouseId, setViewingHouseId] = useState(null);

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");

    const html = document.documentElement;
    const saved = localStorage.getItem("kt_theme_mode") || "light";
    html.setAttribute("data-bs-theme", saved);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  const fetchData = useCallback(async () => {
    if (!estateId) { setIsLoading(false); setPageReady(true); return; }
    setIsLoading(true);
    try {
      const [btRes, bRes, tRes, poRes, summaryRes] = await Promise.all([
        // Building types are estate-admin only; Phase Administrators derive them from their buildings.
        isPhaseAdmin() ? Promise.resolve(null) : authFetch(`${API_BASE}/estates/${estateId}/building-types`),
        authFetch(`${API_BASE}/estates/${estateId}/buildings`),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/with-dependents?pageIndex=0&pageSize=1000`),
        authFetch(`${API_BASE}/estates/${estateId}/primary-occupants?pageIndex=0&pageSize=30`).catch(() => null),
        authFetch(`${API_BASE}/estates/${estateId}/dashboard-summary`).catch(() => null),
      ]);

      const bList = parseArrayData(safeJsonParse(await bRes.text()));
      const btList = btRes
        ? parseArrayData(safeJsonParse(await btRes.text()))
        : [...new Map(bList.filter(b => b.typeId).map(b => [b.typeId, { id: b.typeId, name: b.typeName || b.type || "Unit" }])).values()];
      const tList = parseArrayData(safeJsonParse(await tRes.text()));

      let poList = [];
      if (poRes && poRes.ok) {
        const poJson = safeJsonParse(await poRes.text());
        poList = Array.isArray(poJson?.data?.data)
          ? poJson.data.data
          : (Array.isArray(poJson?.data) ? poJson.data : []);
      }

      if (summaryRes && summaryRes.ok) {
        const summaryJson = safeJsonParse(await summaryRes.text());
        if (summaryJson?.data) setDashboardSummary(summaryJson.data);
      }

      setBuildingTypes(btList);
      setBuildings(bList);

      const mappedTenants = tList.map(t => ({
        id: t.tenantId || t.id,
        firstName: t.firstName || "",
        lastName: t.lastName || "",
        name: `${t.firstName || ""} ${t.lastName || ""}`.trim() || t.name || "Unknown",
        title: t.title || "",
        email: t.email || "",
        phone: t.phoneNumber || t.phone || "",
        occupation: t.occupation || "",
        avatar: t.imageUrl || null,
        buildings: t.currentBuildings || t.assignedBuildings || (t.assignedBuilding ? [t.assignedBuilding] : []),
        dependents: t.dependents || [],
        contactPersons: t.contactPersons || (t.contactPerson ? [t.contactPerson] : []),
      }));
      setTenants(mappedTenants);

      const mappedPOs = poList.map(po => ({
        id: po.id,
        name: `${po.firstName || ""} ${po.lastName || ""}`.trim() || po.name || "Unknown",
        email: po.email || "",
        phone: po.phoneNumber || "",
        avatar: po.imageUrl || null,
      }));
      setPrimaryOccupants(mappedPOs);
    } catch {
      toast.error("Failed to load rent data.");
    } finally {
      setIsLoading(false);
      setPageReady(true);
    }
  }, [estateId]);

  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const fetchedEstateId = useRef(null);
  useEffect(() => {
    if (fetchedEstateId.current === estateId) return;
    fetchedEstateId.current = estateId;
    fetchData();
  }, [fetchData, estateId]);

  useEffect(() => {
    const interval = setInterval(() => {
      const s = getSession();
      if (!s?.accessTokenExpiresAt) return;
      const expiresAt = new Date(s.accessTokenExpiresAt).getTime();
      const timeUntilExpiry = expiresAt - Date.now();
      if (timeUntilExpiry > 0 && timeUntilExpiry < 3 * 60 * 1000) {
        refreshAccessToken().catch(err => console.warn("Background refresh failed", err));
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!estateId) return;
    let isMounted = true;
    setLoadingUpcoming(true);
    authFetch(`${API_BASE}/estates/${estateId}/tenants/upcoming-rent?daysAhead=30&pageIndex=1&pageSize=50`)
      .then(res => res.ok ? res.json() : null)
      .then(applyBillingEnumText)
      .then(json => {
        if (!isMounted) return;
        const items = json?.data?.data || json?.data || [];
        setUpcomingRentTenants(items);
      })
      .catch(err => {
        console.error("Failed to load upcoming rent", err);
      })
      .finally(() => {
        if (isMounted) setLoadingUpcoming(false);
      });
    return () => { isMounted = false; };
  }, [estateId]);

  const filteredBuildings = useMemo(() => {
    const q = buildingSearch.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, "");

    const matchesPhone = (phoneStr) => {
      if (!phoneStr) return false;
      const lowerPhone = String(phoneStr).toLowerCase();
      if (lowerPhone.includes(q)) return true;
      if (qDigits.length >= 3) {
        const cleanPhone = lowerPhone.replace(/\D/g, "");
        const localPhone = cleanPhone.replace(/^234/, "0");
        const noZeroPhone = cleanPhone.replace(/^(234|0)/, "");
        const cleanQ = qDigits;
        const localQ = qDigits.replace(/^234/, "0");
        const noZeroQ = qDigits.replace(/^(234|0)/, "");

        if (cleanPhone.includes(cleanQ) || cleanPhone.includes(localQ) || cleanPhone.includes(noZeroQ)) return true;
        if (localPhone.includes(cleanQ) || localPhone.includes(localQ) || localPhone.includes(noZeroQ)) return true;
        if (noZeroPhone.includes(cleanQ) || noZeroPhone.includes(localQ) || noZeroPhone.includes(noZeroQ)) return true;
      }
      return false;
    };

    const list = buildings.filter(b => {
      const bNum = String(b.number || b.buildingNumber || "").toLowerCase();
      const phase = String(b.phaseName || "").toLowerCase();
      const type = String(b.typeName || "").toLowerCase();
      const occupant = String(b.occupantName || b.tenantName || "").toLowerCase();

      const bId = b.id || b.buildingId;
      const matchingTenants = tenants.filter(t => {
        if (!t.buildings) return false;
        return t.buildings.some(tb => {
          const tbId = tb.id || tb.buildingId;
          if (tbId && bId && String(tbId) === String(bId)) return true;
          if (tb.number && b.number && String(tb.number).trim() === String(b.number).trim()) return true;
          return false;
        });
      });

      let matchesTenantSearch = false;
      if (q) {
        matchesTenantSearch = matchingTenants.some(t => {
          const firstName = (t.firstName || "").toLowerCase();
          const lastName = (t.lastName || "").toLowerCase();
          const fullName = `${firstName} ${lastName}`.trim();
          const name = (t.name || "").toLowerCase();
          const email = (t.email || "").toLowerCase();
          const phone = t.phone || "";

          let m =
            firstName.includes(q) ||
            lastName.includes(q) ||
            fullName.includes(q) ||
            name.includes(q) ||
            email.includes(q) ||
            matchesPhone(phone);

          if (!m && t.dependents && Array.isArray(t.dependents)) {
            m = t.dependents.some(d => {
              const dFirst = (d.firstName || "").toLowerCase();
              const dLast = (d.lastName || "").toLowerCase();
              const dFull = `${dFirst} ${dLast}`.trim();
              const dEmail = (d.email || "").toLowerCase();
              const dPhone = d.phoneNumber || d.phone || "";
              return dFirst.includes(q) || dLast.includes(q) || dFull.includes(q) || dEmail.includes(q) || matchesPhone(dPhone);
            });
          }

          if (!m && t.contactPersons && Array.isArray(t.contactPersons)) {
            m = t.contactPersons.some(cp => {
              const cpFirst = (cp.firstName || "").toLowerCase();
              const cpLast = (cp.lastName || "").toLowerCase();
              const cpFull = `${cpFirst} ${cpLast}`.trim();
              const cpEmail = (cp.email || "").toLowerCase();
              const cpPhone = cp.phoneNumber || cp.phone || "";
              return cpFirst.includes(q) || cpLast.includes(q) || cpFull.includes(q) || cpEmail.includes(q) || matchesPhone(cpPhone);
            });
          }
          return m;
        });
      }

      const matchesSearch = !q || bNum.includes(q) || phase.includes(q) || type.includes(q) || occupant.includes(q) || matchesTenantSearch;
      const matchesPhase = buildingPhaseFilter === "all" || b.phaseName === buildingPhaseFilter;
      const matchesType = buildingTypeFilter === "all" || b.typeName === buildingTypeFilter;

      const matchesRent = (() => {
        if (buildingRentFilter === "all") return true;
        const isDue = checkIsBuildingRentDue(b, upcomingRentTenants);
        const amt = getRentAmount(b.currentRent);
        const hasRentSet = amt !== null && amt !== undefined && amt > 0;
        if (buildingRentFilter === "due") return isDue;
        if (buildingRentFilter === "set") return hasRentSet;
        if (buildingRentFilter === "unset") return !hasRentSet;
        return true;
      })();

      return matchesSearch && matchesPhase && matchesType && matchesRent;
    });

    if (buildingSortBy === "phase") {
      return [...list].sort((a, b) => {
        const phaseA = String(a.phaseName || "");
        const phaseB = String(b.phaseName || "");
        if (!phaseA && !phaseB) return 0;
        if (!phaseA) return 1;
        if (!phaseB) return -1;
        const compPhase = phaseA.localeCompare(phaseB, undefined, { numeric: true, sensitivity: "base" });
        if (compPhase !== 0) return compPhase;
        const numA = String(a.number || a.buildingNumber || "");
        const numB = String(b.number || b.buildingNumber || "");
        return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
      });
    }

    return [...list].sort((a, b) => {
      const numA = String(a.number || a.buildingNumber || "");
      const numB = String(b.number || b.buildingNumber || "");
      if (!numA && !numB) return 0;
      if (!numA) return 1;
      if (!numB) return -1;
      return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [buildings, tenants, buildingSearch, buildingPhaseFilter, buildingTypeFilter, buildingRentFilter, buildingSortBy, upcomingRentTenants]);

  const totalBuildingPages = Math.max(1, Math.ceil(filteredBuildings.length / BUILDING_PAGE_SIZE));
  const pagedBuildings = useMemo(() => {
    const safe = Math.min(Math.max(buildingPage, 1), totalBuildingPages);
    return filteredBuildings.slice((safe - 1) * BUILDING_PAGE_SIZE, safe * BUILDING_PAGE_SIZE);
  }, [filteredBuildings, buildingPage, totalBuildingPages]);
  useEffect(() => setBuildingPage(1), [buildingSearch, buildingPhaseFilter, buildingTypeFilter, buildingRentFilter, buildingSortBy]);

  const uniquePhases = useMemo(() => {
    const phases = buildings.map(b => b.phaseName).filter(Boolean);
    return [...new Set(phases)].sort();
  }, [buildings]);

  const openSetRentModal = (buildingId = null) => {
    setSelectedBuildingId(buildingId);
    setRentModal.show();
  };

  const openHouseRentDetailsModal = (buildingId) => {
    setViewingHouseId(buildingId);
    houseRentDetailsModal.show();
  };

  return (
    <>
      <Dashboard6Styles />

      <PagePreloader visible={!pageReady} />

      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">

          {/* Header */}
          <div id="kt_app_header" className="app-header" data-kt-sticky="true" data-kt-sticky-activate="{default: false, lg: true}" data-kt-sticky-name="app-header-sticky" data-kt-sticky-offset="{default: false, lg: '300px'}">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2"></i>
                </div>
                <Link to="/dashboard"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>

              <PageHeader title="Rent Management">
                <button type="button" className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => downloadModal.show()}>
                  <i className="ki-outline ki-file-down fs-6 me-sm-2"></i>
                  <span className="d-none d-sm-inline">Download Rent Log</span>
                </button>
                <button type="button" className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => assignBuildingModal.show()}>
                  <i className="ki-outline ki-add-item fs-6 me-sm-2"></i>
                  <span className="d-none d-sm-inline">Assign Building</span>
                </button>
                <button type="button" className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => generateInvoiceModal.show()}>
                  <i className="ki-outline ki-document fs-6 me-sm-2"></i>
                  <span className="d-none d-sm-inline">Generate Invoice</span>
                </button>
                <button type="button" className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => openSetRentModal(null)}>
                  <i className="ki-outline ki-wallet fs-6 me-sm-2 text-white"></i>
                  <span className="d-none d-sm-inline">Set Rent</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">

                    <KpiStrip
                      loading={isLoading}
                      items={[
                        { icon: "ki-wallet", label: "Total Rent Collected", value: fmtMoney(dashboardSummary.totalRentCollected), sub: "Lifetime revenue" },
                        { icon: "ki-calendar", label: "Collected This Month", value: fmtMoney(dashboardSummary.currentMonthRentCollected), sub: "Revenue this month" },
                        { icon: "ki-time", label: "Pending Rent", value: fmtMoney(dashboardSummary.pendingRent), sub: "Outstanding rent" },
                        { icon: "ki-information-2", label: "Upcoming Rent", value: loadingUpcoming ? "—" : upcomingRentTenants.length, sub: "Due in 30 days" },
                      ]}
                    />

                    <div className="d6-card p-6 mb-5">
                      <CardHead title="All Estate Buildings" subtitle="Set or update rent for that building" className="mb-5">
                        <div className="position-relative">
                          <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
                          <input className="d6m-input ps-10" style={{ width: 260, minHeight: 40, height: 40, borderRadius: 999 }} placeholder="Search building number, phase…" value={buildingSearch} onChange={e => setBuildingSearch(e.target.value)} />
                        </div>
                        <select className="d6m-input" style={{ width: 140, minHeight: 40, height: 40, borderRadius: 999 }} value={buildingPhaseFilter} onChange={e => setBuildingPhaseFilter(e.target.value)}>
                          <option value="all">All Phases</option>
                          {uniquePhases.map(phase => <option key={phase} value={phase}>{phase}</option>)}
                        </select>
                        <select className="d6m-input" style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }} value={buildingTypeFilter} onChange={e => setBuildingTypeFilter(e.target.value)}>
                          <option value="all">All Types</option>
                          {buildingTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                        </select>
                        <select className="d6m-input" style={{ width: 170, minHeight: 40, height: 40, borderRadius: 999 }} value={buildingRentFilter} onChange={e => setBuildingRentFilter(e.target.value)}>
                          <option value="all">All Rent Statuses</option>
                          <option value="due">Rent Due</option>
                          <option value="set">Rent Set</option>
                          <option value="unset">No Rent Set</option>
                        </select>
                      </CardHead>

                      <div>
                        {isLoading ? (
                          <div className="row g-5">
                            {[1, 2, 3, 4, 5, 6].map(n => (
                              <div key={n} className="col-12 col-md-6 col-xl-4">
                                <div className="d6-card h-100 p-5 placeholder-glow">
                                  <div className="placeholder rounded-3 w-100 mb-4" style={{ height: CARD_IMG_HEIGHT }}></div>
                                  <div className="placeholder col-8 h-20px rounded mb-2"></div>
                                  <div className="placeholder col-5 h-15px rounded"></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : filteredBuildings.length === 0 ? (
                          <div className="d6m-empty py-12">
                            <i className="ki-outline ki-home-2 fs-3x text-gray-300 d-block mb-3"></i>
                            <div className="d6-title text-gray-900 mb-1">No buildings found</div>
                            <div className="fs-8 text-gray-500">Add buildings in Estate Setup or adjust your search.</div>
                          </div>
                        ) : (
                          <>
                            <div className="row g-5">
                              {pagedBuildings.map(b => {
                                const rentAmt = getRentAmount(b.currentRent);
                                const isDue = checkIsBuildingRentDue(b, upcomingRentTenants);
                                return (
                                  <div key={b.id} className="col-12 col-md-6 col-xl-4">
                                    {(() => {
                                      const type = buildingTypes.find(t => t.id === b.typeId) || {};
                                      const fallbackImg = getHouseImage(b.typeName);
                                      const imgUrl = (type.imageUrl && !type.imageUrl.includes("2bd.png")) ? type.imageUrl : fallbackImg;
                                      const cr = b.currentRent && typeof b.currentRent === "object" ? b.currentRent : {};
                                      const total = Number(cr.totalAmount || b.totalAmount || 0);
                                      let svc = total > Number(rentAmt || 0) ? total - Number(rentAmt || 0) : Number(b.serviceCharge || cr.serviceCharge || 0);
                                      if (!svc) {
                                        try {
                                          const stored = JSON.parse(localStorage.getItem(`zangon_building_charges_${b.id}`) || "[]");
                                          if (Array.isArray(stored)) svc = stored.reduce((sum, c) => sum + Number(c.amount || 0), 0);
                                        } catch { /* ignore */ }
                                      }
                                      const assignedOn = b.assignedAt || b.assignedDate || cr.assignedAt || cr.startDate || cr.createdAt;
                                      return (
                                        <div
                                          role="button"
                                          tabIndex={0}
                                          className={`d6-card h-100 d-flex flex-column cursor-pointer ${isDue ? "border-hover-danger" : "border-hover-success"} transition-all overflow-hidden`}
                                          onClick={() => openHouseRentDetailsModal(b.id)}
                                          onKeyDown={(e) => { if (e.key === "Enter") openHouseRentDetailsModal(b.id); }}
                                        >
                                          <div className="position-relative overflow-hidden" style={{ height: 180 }}>
                                            <img
                                              alt={b.typeName}
                                              src={imgUrl}
                                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImg; }}
                                            />
                                            <div className="position-absolute" style={{ inset: 0, background: "linear-gradient(rgba(0,0,0,0) 50%, rgba(0,0,0,0.65) 100%)" }} />
                                            <div className="position-absolute bottom-0 start-0 p-3 d-flex align-items-center gap-2">
                                              {b.phaseName && <span className="badge bg-success text-white fs-8 fw-semibold rounded-pill px-3 py-1">{b.phaseName}</span>}
                                              {(b.number || b.buildingNumber) && <span className="badge bg-white text-success fs-8 fw-semibold rounded-pill px-3 py-1">House {b.number || b.buildingNumber}</span>}
                                            </div>
                                          </div>
                                          <div className="p-4 d-flex flex-column flex-grow-1">
                                            <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                              <div className="min-w-0">
                                                <h4 className="d6-title text-gray-900 mb-1 text-truncate">{b.typeName}</h4>
                                                <span className="d6m-muted fs-8">{assignedOn ? `Assigned ${fmtDate(assignedOn)}` : "Not assigned"}</span>
                                              </div>
                                              <span className="d6m-chip" style={{ "--dot": isDue ? "#DC2626" : "#16A34A" }}>{isDue ? "Due" : "Active"}</span>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between mt-auto pt-3 border-top border-gray-100">
                                              <div className="d-flex flex-column min-w-0">
                                                <span className="d6m-k mb-0 fs-9">Base Rent</span>
                                                <div className="d-flex align-items-baseline gap-2">
                                                  <span className="d6-title text-success fs-7 mb-0">{fmtMoney(rentAmt)}</span>
                                                  {svc > 0 && <span className="text-gray-400 fs-9 text-truncate">+{fmtMoney(svc)} Service Charge</span>}
                                                </div>
                                              </div>
                                              <div className="d-flex align-items-center gap-2">
                                                <span className="d6-icon-btn" title="View details"><i className="ki-outline ki-arrow-up-right fs-5"></i></span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })}
                            </div>

                            <Pagination
                              page={buildingPage}
                              totalPages={totalBuildingPages}
                              onChange={setBuildingPage}
                              summary={`Showing ${(buildingPage - 1) * BUILDING_PAGE_SIZE + 1}–${Math.min(buildingPage * BUILDING_PAGE_SIZE, filteredBuildings.length)} of ${filteredBuildings.length} buildings`}
                            />
                          </>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-4">
                  <div className="text-gray-900 text-center fs-6">
                    <span className="text-gray-600 fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary fw-bold">Powered by Pixels Solutions Limited</a>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up"></i>
          </div>
        </div>
      </div>

      <SetRentModal
        modalRef={setRentModalRef}
        estateId={estateId}
        buildings={buildings}
        buildingTypes={buildingTypes}
        initialBuildingId={selectedBuildingId}
        onSuccess={handleRefresh}
      />
      <GenerateInvoiceModal
        modalRef={generateInvoiceModalRef}
        estateId={estateId}
        tenants={tenants}
        buildings={buildings}
        onSuccess={handleRefresh}
      />
      <AssignBuildingModal
        modalRef={assignBuildingModalRef}
        estateId={estateId}
        tenants={tenants}
        primaryOccupants={primaryOccupants}
        buildings={buildings}
        onSuccess={handleRefresh}
      />
      <HouseRentDetailsModal
        modalRef={houseRentDetailsModalRef}
        estateId={estateId}
        buildingId={viewingHouseId}
        buildings={buildings}
        tenants={tenants}
        buildingTypes={buildingTypes}
        onEditRent={openSetRentModal}
        onViewInvoice={(inv) => { houseRentDetailsModal.hide(); setSelectedInvoice(inv); }}
        onViewPayment={(pay) => { houseRentDetailsModal.hide(); setSelectedPayment(pay); }}
      />
      <DownloadRentLogModal
        modalRef={downloadModalRef}
        buildings={buildings}
        upcomingRentTenants={upcomingRentTenants}
        fmtMoney={fmtMoney}
      />
      {selectedInvoice && (
        <InvoiceDetailModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
      {selectedPayment && (
        <PaymentReceiptModal
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </>
  );
}